import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { BookOpen, Clock, CheckCircle, ChevronDown, Play, ArrowRight } from 'lucide-react';
import { curriculum, courseHighlights, skills } from '../data/mock';

const Curriculum = ({ onModuleSelect }) => {
  const [expandedModule, setExpandedModule] = useState("module-1");

  const handleStartModule = (moduleId) => {
    onModuleSelect && onModuleSelect(moduleId);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Comprehensive Curriculum</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            What You&apos;ll Master
          </h2>
          <p className="text-slate-600">
            <span className="font-semibold text-slate-800">8 modules</span> · 
            <span className="font-semibold text-slate-800"> 10 weeks</span> · 
            <span className="font-semibold text-slate-800"> 40+ hours</span> of content
          </p>
        </div>

        {/* Curriculum accordion */}
        <div className="max-w-4xl mx-auto mb-16">
          <Accordion type="single" collapsible value={expandedModule} onValueChange={setExpandedModule}>
            {curriculum.map((module, index) => (
              <AccordionItem 
                key={module.id} 
                value={`module-${module.id}`}
                className="mb-4 border border-slate-200 rounded-xl overflow-hidden bg-white hover:border-emerald-300 transition-colors duration-300"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline group">
                  <div className="flex items-center gap-4 w-full">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">
                        {module.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {module.duration} · {module.topicCount} topics
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <div className="ml-16 grid sm:grid-cols-2 gap-2 mb-4">
                    {module.topics.map((topic, topicIndex) => (
                      <div 
                        key={topicIndex}
                        className="flex items-start gap-2 text-slate-600 text-sm py-1"
                      >
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{topic}</span>
                      </div>
                    ))}
                  </div>
                  <div className="ml-16 flex gap-3">
                    <Link to={`/module/${module.id}`}>
                      <Button 
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      >
                        <ArrowRight className="w-3 h-3 mr-2" />
                        Start Module
                      </Button>
                    </Link>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => handleStartModule(module.id)}
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                    >
                      <Play className="w-3 h-3 mr-2" />
                      Take Quiz
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Course highlights */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Highlights stats */}
            <div>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                Course Highlights
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {courseHighlights.map((highlight, index) => (
                  <div key={index} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="text-2xl font-bold text-emerald-400">{highlight.value}</div>
                    <div className="text-sm text-slate-400">{highlight.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div>
              <h3 className="text-xl font-bold text-white mb-6">Skills You&apos;ll Gain</h3>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium hover:bg-emerald-500/20 transition-colors cursor-default"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Curriculum;
